import Alert from '@material-ui/lab/Alert'
import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'

const useStyles = makeStyles((theme) => ({
    errorMessageContainer: {
        [theme.breakpoints.up('md')]: {
            maxWidth: 500,
        },
        marginTop: 20,
        marginBottom: 20,
        borderStyle: 'solid',
        borderColor: '#EBA339',
        borderRadius: 10,
        borderWidth: 2,
        backgroundColor: '#FFF',
        '& .MuiAlert-message': {
            width: '100%',
        },
    },
    closeButtonWrapper: {
        padding: 0,
        display: 'flex',
        justifyContent: 'flex-end',
    },
    closeButton: {
        color: 'white',
        backgroundColor: '#6f6f6f',
        padding: 0,
        width: '1.5rem',
        height: '1.5rem',
    },
}))

// A redirect return's failure message rides its own state instead of the shared errors, because the
// fresh page load is full of things that clear that state (clearErrors, focus handlers, the provider
// re-key remounting the form). Same yellow box; only the lifecycle differs — the buyer dismisses it.
export const ReturnFailureAlert = ({ message, onClose }) => {
    const classes = useStyles()

    if (!message) {
        return null
    }

    return (
        <Alert
            severity="warning"
            className={classes.errorMessageContainer}
            id="return-failure-alert"
        >
            <Grid container spacing={2}>
                <Grid item xs={10}>
                    <strong style={{ fontSize: 'large' }}>Payment Issue</strong>
                </Grid>
                <Grid item xs={2} className={classes.closeButtonWrapper}>
                    <IconButton className={classes.closeButton} onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Grid>
                <Grid item xs={10}>
                    {message}
                </Grid>
            </Grid>
        </Alert>
    )
}

const CheckoutErrorAlert = ({
    errors,
    errorCode,
    showCardError,
    handleClick,
}) => {
    const classes = useStyles()

    if (!showCardError || !handleClick) {
        console.log('(!showCardError || !handleClick) == true')
        return <></>
    }

    const isPhoneNumberRequiredError =
        Array.isArray(errors) &&
        errors.some((error) => error.includes('phone number is required'))
    if (isPhoneNumberRequiredError) {
        console.log('(isPhoneNumberRequiredError) == true')
    }

    const isPayPalCrossDomainWindowError =
        Array.isArray(errors) &&
        errors.some((error) => error.includes('cross-domain window'))
    if (isPayPalCrossDomainWindowError) {
        console.log('(isPayPalCrossDomainWindowError) == true')
    }

    // The pre-submit billing address checks. `every`, not `some`: these messages name the exact
    // field to fix, so they render verbatim — a mixed batch falls through to the generic copy.
    const isBillingAddressError =
        Array.isArray(errors) &&
        errors.length > 0 &&
        errors.every(
            (error) =>
                typeof error === 'string' &&
                (error.includes('billing address') ||
                    error.includes('full name') ||
                    error.includes('Address line') ||
                    error.includes('City'))
        )

    const errorHeaderAndMessage = {
        insufficient_funds: {
            heading: 'Processing Issue',
            errorMessage: `We had a problem processing this payment. Please contact your issuing bank and try again or select another payment method.`,
        },
        generic_payment_method_error: {
            heading: 'Payment Issue',
            errorMessage: `We had a problem processing this credit/debit card. Please verify all your information is correct and try again or select another payment method.`,
        },
        expired_card: {
            heading: 'Card Expired',
            errorMessage: `We had a problem processing this payment. Please verify all your information is correct and try again or select another payment method.`,
        },
        not_permitted: {
            heading: 'Purchase Not Permitted',
            errorMessage: `We had a problem processing this payment. Please contact your issuing bank and try again or select another payment method.`,
        },
        restricted_card: {
            heading: 'Card Declined',
            errorMessage: `We had a problem processing this payment. Please contact your issuing bank and try again or select another payment method.`,
        },
        auth_failed: {
            heading: 'Payment Declined',
            errorMessage: `Your card has been declined. Please try another payment
                        method.`,
        },
        generic_decline: {
            heading: 'Payment Declined',
            errorMessage: `Your card has been declined. Please try another payment
                        method.`,
        },
    }
    
    if (isPhoneNumberRequiredError) {
        return (
            <Alert
                severity="warning"
                className={classes.errorMessageContainer}
                id="checkout-error-alert"
            >
                <Grid container spacing={2}>
                    <Grid item xs={10}>
                        <strong style={{ fontSize: 'large' }}>Error</strong>
                    </Grid>
                    <Grid item xs={2} className={classes.closeButtonWrapper}>
                        <IconButton
                            className={classes.closeButton}
                            onClick={handleClick}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                    <Grid item xs={10}>
                        Whoops! A phone number is required to complete this
                        order.
                    </Grid>
                </Grid>
            </Alert>
        )
    } else if (isPayPalCrossDomainWindowError) {
        return (
            <Alert
                severity="warning"
                className={classes.errorMessageContainer}
                id="checkout-error-alert"
            >
                <Grid container spacing={2}>
                    <Grid item xs={10}>
                        <strong style={{ fontSize: 'large' }}>Error</strong>
                    </Grid>
                    <Grid item xs={2} className={classes.closeButtonWrapper}>
                        <IconButton
                            className={classes.closeButton}
                            onClick={handleClick}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                    <Grid item xs={10}>
                        Whoops! Something went wrong while processing your
                        payment. Please try refreshing the page and attempting
                        the payment again. If the issue persists, contact our
                        support team and provide them with the following error
                        code: zoid_paypal_checkout.
                    </Grid>
                </Grid>
            </Alert>
        )
    } else if (isBillingAddressError) {
        return (
            <Alert
                severity="warning"
                className={classes.errorMessageContainer}
                id="checkout-error-alert"
            >
                <Grid container spacing={2}>
                    <Grid item xs={10}>
                        <strong style={{ fontSize: 'large' }}>
                            Billing Address
                        </strong>
                    </Grid>
                    <Grid item xs={2} className={classes.closeButtonWrapper}>
                        <IconButton
                            className={classes.closeButton}
                            onClick={handleClick}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                    <Grid item xs={10}>
                        {errors.map((error) => (
                            <div key={error}>{error}</div>
                        ))}
                    </Grid>
                </Grid>
            </Alert>
        )
    } else if (
        Array.isArray(errors) &&
        errors.includes('Your card number is invalid.')
    ) {
        return (
            <Alert
                severity="warning"
                className={`${classes.errorMessageContainer} ${classes.boldText}`}
                id="checkout-error-alert"
            >
                <Grid container spacing={2}>
                    <Grid item xs={10}>
                        <strong style={{ fontSize: 'large' }}>
                            Payment Issue
                        </strong>
                    </Grid>
                    <Grid item xs={2} className={classes.closeButtonWrapper}>
                        <IconButton
                            className={classes.closeButton}
                            onClick={handleClick}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                    <Grid item xs={10}>
                        We had a problem processing this credit/debit card.
                        Please verify all your information is correct and try
                        again or select another payment method.
                    </Grid>
                </Grid>
            </Alert>
        )
    } else if (
        Array.isArray(errors) &&
        errors.includes('An unexpected error occurred while applying your promo code. Please try again.')
    ) {
        return (
            <Alert
                severity="warning"
                className={`${classes.errorMessageContainer} ${classes.boldText}`}
                id="checkout-error-alert"
            >
                <Grid container spacing={2}>
                    <Grid item xs={10}>
                        <strong style={{ fontSize: 'large' }}>
                            Promo Code Issue
                        </strong>
                    </Grid>
                    <Grid item xs={2} className={classes.closeButtonWrapper}>
                        <IconButton
                            className={classes.closeButton}
                            onClick={handleClick}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                    <Grid item xs={10}>
                        The promo code provided has already been used by this account. Please remove it and try again.
                    </Grid>
                </Grid>
            </Alert>
        )
    }  else if (errorHeaderAndMessage[errorCode]) {
        const { heading, errorMessage } = errorHeaderAndMessage[errorCode]
        return (
            <Alert
                severity="warning"
                className={classes.errorMessageContainer}
                id="checkout-error-alert"
            >
                <Grid container spacing={2}>
                    <Grid item xs={10}>
                        <strong style={{ fontSize: 'large' }}>{heading}</strong>
                    </Grid>
                    <Grid item xs={2} className={classes.closeButtonWrapper}>
                        <IconButton
                            className={classes.closeButton}
                            onClick={handleClick}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                    <Grid item xs={10}>
                        {errorMessage}
                    </Grid>
                </Grid>
            </Alert>
        )
    } else if (Array.isArray(errors) && errors.length > 0) {
        return (
            <Alert
                severity="warning"
                className={classes.errorMessageContainer}
                id="checkout-error-alert"
            >
                <Grid container spacing={2}>
                    <Grid item xs={10}>
                        <strong style={{ fontSize: 'large' }}>Error</strong>
                    </Grid>
                    <Grid item xs={2} className={classes.closeButtonWrapper}>
                        <IconButton
                            className={classes.closeButton}
                            onClick={handleClick}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                    <Grid item xs={10}>
                        We had a problem processing this order. Please verify
                        all your information is correct and try again or select
                        another payment method.
                    </Grid>
                </Grid>
            </Alert>
        )
    } else {
        return <></>
    }
}

export default CheckoutErrorAlert
